// src/components/PeoplePlan/EmployeeManagement/hooks/useWorkflowManagement.js
import { useState, useEffect } from 'react';
import apiClient from '../../../../apiClient';
import { WORKFLOW_REGISTRY } from '../../configs/workflowRegistry';
import { peoplePulseDataService } from '../../../../pages/LD/PeoplePulse/utils/peoplePulseDataService';

export const useWorkflowManagement = () => {
  const [selectedWorkflow, setSelectedWorkflow] = useState("");
  const [workflowDescription, setWorkflowDescription] = useState(null);
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [conflictEmployees, setConflictEmployees] = useState([]);
  const [currentUserName, setCurrentUserName] = useState({ name: "", username: "" });

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await apiClient.get('/api/users/me');
      const data = response.data;
      setCurrentUserName({
        name: data.name || "Unknown User",
        username: data.username || "Unknown UserName",
      });
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const handleWorkflowChange = (workflowKey) => {
    setSelectedWorkflow(workflowKey);
    const workflow = WORKFLOW_REGISTRY[workflowKey];
    setWorkflowDescription(workflow || null);
  };

  const checkWorkflowConflicts = async (userIds) => {
    if (!selectedWorkflow || userIds.length === 0) {
      return false;
    }

    const workflow = WORKFLOW_REGISTRY[selectedWorkflow];
    if (!workflow) {
      return false;
    }

    try {
      const response = await apiClient.post('/api/workflows/check', {
        workflow_id: workflow.id,
        user_ids: userIds,
      });

      const checkResult = response.data;
      const duplicates = checkResult.duplicates || [];
      setConflictEmployees(duplicates);

      return duplicates.length === 0;
    } catch (error) {
      console.error("Error checking workflow conflicts:", error);
      throw error;
    }
  };

  // Handle PeoplePulse specific workflows
  const handlePeoplePulseWorkflow = async (workflow, userIds, formData, employeeData = null) => {
    const userId = userIds[0]; // Get the first selected employee

    switch (workflow.id) {
      case 13: // Create Goals & OKRs
        return await peoplePulseDataService.createGoal({
          user_id: userId,
          title: formData.goalTitle,
          description: formData.goalDescription,
          type: formData.goalType,
          category: formData.goalCategory || null,
          start_date: formData.goalStartDate,
          due_date: formData.goalDueDate,
          created_by: currentUserName.username,
        });

      case 14: // Create 1 on 1 Meeting
        // Pass userId as third parameter to ensure we can always fetch email
        return await scheduleMeeting(formData, employeeData, userId);

      case 15: // Create Performance Review
        return await peoplePulseDataService.createReviewCycle({
          user_id: userId,
          cycle_name: formData.cycleName,
          review_type: formData.reviewType,
          template: formData.reviewTemplate,
          start_date: formData.reviewStartDate,
          end_date: formData.reviewEndDate,
          created_by: currentUserName.username,
        });

      case 16: // Create Feedback & Recognition
        if (formData.feedbackType === 'Feedback') {
          return await peoplePulseDataService.giveFeedback({
            recipient_id: userId,
            feedback_type: 'feedback',
            message: formData.feedbackMessage,
            given_by: currentUserName.username,
          });
        } else {
          return await peoplePulseDataService.giveRecognition({
            recipient_id: userId,
            message: formData.recognitionMessage,
            given_by: currentUserName.username,
          });
        }

      default:
        throw new Error(`Unknown PeoplePulse workflow: ${workflow.id}`);
    }
  };

  // Schedule a 1-on-1 meeting via calendar API
  // Added userId as third parameter for guaranteed fallback
  const scheduleMeeting = async (formData, employeeData, userId) => {
    // Parse duration to minutes
    const durationMap = {
      "15 min": 15,
      "30 min": 30,
      "45 min": 45,
      "60 min": 60,
    };
    const durationMinutes = durationMap[formData.meetingDuration] || 30;

    // Parse start datetime
    const startDateTime = new Date(formData.meetingDateTime);
    const endDateTime = new Date(startDateTime.getTime() + durationMinutes * 60000);

    // Format dates for API (ISO format)
    const startISO = startDateTime.toISOString();
    const endISO = endDateTime.toISOString();

    // Build meeting subject
    const subject = `1:1 ${formData.meetingType} - ${employeeData?.employee_name || 'Employee'}`;

    // Build meeting body/agenda
    const body = `
      <h2>${formData.meetingType}</h2>
      <p><strong>Employee:</strong> ${employeeData?.employee_name || 'N/A'}</p>
      <p><strong>Duration:</strong> ${formData.meetingDuration}</p>
      ${formData.meetingAgenda ? `<h3>Agenda / Notes:</h3><p>${formData.meetingAgenda.replace(/\n/g, '<br/>')}</p>` : ''}
      <hr/>
      <p><em>Scheduled via People Plan</em></p>
    `;

    // Build attendees list (the selected employee)
    const attendees = [];

    // Try to get the employee email
    let employeeEmail = employeeData?.email;

    // Determine which user_id to use - prefer employeeData, fallback to passed userId
    const effectiveUserId = employeeData?.user_id || userId;

    console.log(`🔍 Meeting scheduling - employeeData:`, employeeData);
    console.log(`🔍 Meeting scheduling - effectiveUserId: ${effectiveUserId}`);

    // If no email in employeeData, fetch it from the API
    if (!employeeEmail && effectiveUserId) {
      try {
        const emailResponse = await apiClient.get(`/api/employee-email/${effectiveUserId}`);
        if (emailResponse.data?.email) {
          employeeEmail = emailResponse.data.email;
          console.log(`📧 Fetched employee email: ${employeeEmail} (source: ${emailResponse.data.source})`);
        } else {
          console.warn(`⚠️ API returned no email for user_id: ${effectiveUserId}`, emailResponse.data);
        }
      } catch (error) {
        console.warn('Could not fetch employee email:', error);
      }
    } else if (!effectiveUserId) {
      console.error('❌ No user_id available to fetch employee email');
    }

    if (employeeEmail) {
      attendees.push({
        emailAddress: {
          address: employeeEmail,
          name: employeeData?.employee_name || employeeEmail,
        },
        type: "required",
      });
      console.log(`📅 Adding attendee to meeting: ${employeeEmail}`);
    } else {
      console.warn('⚠️ No email found for employee, meeting will be created without attendees');
    }

    // Call the calendar API
    const response = await apiClient.post('/api/calendar/events', {
      subject,
      start: startISO,
      end: endISO,
      location: 'Virtual / Teams',
      body,
      attendees,
    });

    return {
      success: true,
      message: `Meeting scheduled successfully for ${startDateTime.toLocaleString()}`,
      event: response.data.event,
    };
  };

  const startWorkflow = async (userIds, formData, roleConfig, selectedMUM, employeeData = null) => {
    const workflow = WORKFLOW_REGISTRY[selectedWorkflow];
    if (!workflow) throw new Error("Invalid workflow");

    setIsSubmitting(true);

    try {
      // Handle PeoplePulse workflows (Goals, Reviews, Feedback/Recognition, Meetings)
      if (workflow.isPeoplePulseWorkflow && !workflow.isNavigationWorkflow) {
        // For meeting workflow, we need employee data for attendees
        let empData = employeeData;

        // For meeting workflow, ensure we have employee data with user_id
        if (workflow.isMeetingWorkflow) {
          // If no employee data or missing user_id, fetch it
          if (!empData || !empData.user_id) {
            if (roleConfig?.apiEndpoint) {
              const response = await apiClient.get(
                `${roleConfig.apiEndpoint}?mum=${encodeURIComponent(selectedMUM)}&ts=${Date.now()}`
              );
              const data = response.data;
              for (const store of data) {
                const foundEmp = store.employees?.find(emp => emp.user_id === userIds[0]);
                if (foundEmp) {
                  empData = foundEmp;
                  break;
                }
              }
            }
          }

          // Ensure user_id is always set (use from userIds if not in empData)
          if (empData && !empData.user_id) {
            empData = { ...empData, user_id: userIds[0] };
          } else if (!empData) {
            // Create minimal empData with just user_id so we can fetch email
            empData = { user_id: userIds[0] };
          }
        }

        const result = await handlePeoplePulseWorkflow(workflow, userIds, formData, empData);
        setShowTransition(true);
        return result;
      }

      // Special handling for Position Training workflow (ID: 12)
      if (workflow.id === 12) {
        const response = await apiClient.post('/api/workflows/start', {
          workflow_id: workflow.id,
          user_ids: userIds,
          fields: formData,
          note: formData.workflowNote || null,
          role_context: roleConfig.role_context || roleConfig.singularName || roleConfig.displayName,
        });

        const result = response.data;
        setShowTransition(true);
        return result;
      }

      // Regular workflow handling for other workflows
      const response = await apiClient.get(
        `${roleConfig.apiEndpoint}?mum=${encodeURIComponent(
          selectedMUM
        )}&ts=${Date.now()}`
      );

      const data = response.data;

      // Find selected employee
      let employee = null;
      for (const store of data) {
        employee = store.employees.find(emp => emp.user_id === userIds[0]);
        if (employee) break;
      }

      if (!employee) {
        throw new Error("Could not find employee data");
      }

      // Extract the workflow note from formData
      const workflowNote = formData.workflowNote || null;

      // Build fields for SharePoint if needed
      let fields = null;
      if (workflow.requiresSharePoint && formData) {
        fields = await workflow.buildSharePointFields(
          formData,
          employee,
          roleConfig,
          currentUserName,
          workflowNote
        );
      }

      // Start the workflow with the note
      const workflowResponse = await apiClient.post('/api/workflows/start', {
        workflow_id: workflow.id,
        user_ids: userIds,
        fields: workflow.requiresSharePoint ? fields : formData,
        note: workflowNote,
      });

      const result = workflowResponse.data;
      setShowTransition(true);
      return result;

    } finally {
      setIsSubmitting(false);
      setShowConfirmDialog(false);
    }
  };

  return {
    selectedWorkflow,
    workflowDescription,
    showWorkflow,
    setShowWorkflow,
    isSubmitting,
    showConfirmDialog,
    setShowConfirmDialog,
    showTransition,
    setShowTransition,
    conflictEmployees,
    currentUserName,
    handleWorkflowChange,
    checkWorkflowConflicts,
    startWorkflow,
  };
};